import { Button, DatePicker, Divider, Form, Input, Modal, Select } from "components";
import dayjs from "dayjs";
import { useAppSelector } from "hooks";
import { useEffect, useState } from "react";
import { Col, Row, Modal as M } from "react-bootstrap";
import { activeEnum, walletTypeEnum } from "utils/enums";
import { formatCurrency } from "utils/formatter";

interface AddHuntersI {
    okHandler: (object:any) => void;
    visible: boolean;
    closeModal: () => void;
    loading?: boolean;
    currentWallet?: any;
}

const CODE_MIN_LENGTH = 5;
const CODE_MAX_LENGTH = 15;

const AddWallet = ({ okHandler, visible, closeModal, loading, currentWallet }: AddHuntersI) => {
    // Redux constants
    const { products } = useAppSelector(({ catalogs }) => catalogs);
    // status component constants
    const [code, setCode] = useState("");
    const [remaining_uses, setRemainingUses] = useState("");
    const [discount, setDiscount] = useState<string>("0");
    const [productId, setProductId] = useState<number | string>("");
    const [startAt, setStartAt] = useState(dayjs());
    const [expireAt, setExpireAt] = useState(dayjs());
    const [statusEvent, setStatusEvent] = useState("");
    const [type, setType] = useState("");
    const [brand, setBrand] = useState("");
    // events handler
    const handleChange = (e: any) => {
        const { id, value } = e.target;
        switch (id) {
            case "brand":
                setBrand(value.trim());
                break;
            case "code":
                setCode(value ? value.toUpperCase().trim() : '');
                break;
            case 'description':
                setRemainingUses(value);
                break
            default:
                return null;
        }
    }

    const handleDateTimePicker = (typed:string) => (date:any) => {
        if (typed === "start")
            setStartAt(date);
        else
            setExpireAt(date);
    }

    const handleNumberInput = (e:any) => {
        if (e.target.value === "$") {
            setDiscount("0");
        }
        // Remover $, comas y espacios (mantenemos un punto decimal)
        const cleanedValue = e.target.value.replace(/[$,\s]/g, '');
        // Validar que el valor limpio sea números con opcional decimal
        const numberRegex = /^\d+(\.\d*)?$/;
        
        if (cleanedValue && numberRegex.test(cleanedValue)) {
            setDiscount(formatCurrency(cleanedValue));
        }
    }

    const handleSelect = (typed: string) => (value:any) => {
        switch (typed) {
            case "product_id":
                setProductId(value);
                break;
            case 'statusEvent':
                setStatusEvent(value);
                break;
            case 'type':
                setType(value);
                break
            default:
                return null;
        }
    }

    const handleSubmit = (e:any) => {
        e.preventDefault();
        
        okHandler({
            wallet: {
                code,
                id: currentWallet?.id || undefined,
                remaining_uses,
                expires_at: expireAt,
                starts_at: startAt,
                status: statusEvent === "active",
                type,
                brand,
                product_id: type === "Product" && productId ? Number(productId) : null,
                amount: Number(discount.replaceAll(",", "")),
            }
        });
    }

    const handleClearState = () => {
        setCode("");
        setRemainingUses("");
        setDiscount("");
        setProductId("");
        setDiscount("");
        setStartAt(dayjs());
        setExpireAt(dayjs());
        setStatusEvent("");
    }

    const handleHideModal = () => {
        handleClearState();
        closeModal();
        
    }

    useEffect(() => {
        if (!visible) {
            handleClearState();
        }
    }, [visible])

    useEffect(() => {
        if (currentWallet && Object.keys(currentWallet).length > 0) {
            setCode(currentWallet.code);
            setRemainingUses(currentWallet.remaining_uses);
            setStartAt(dayjs(currentWallet.starts_at));
            setExpireAt(dayjs(currentWallet.expires_at));
            setDiscount(currentWallet.amount.toString());
            setProductId(currentWallet.product_id);
            setStatusEvent(currentWallet.status ? "active" : "inactive");
            setType(currentWallet.type);
            setBrand(currentWallet.brand || "");
        }
    }, [currentWallet])
   
    return (
        <Modal show={visible} centered size="lg"
            onHide={handleHideModal} backdrop="static" keyboard={false}
        >
            <M.Header closeButton>
                <h6 className="m-0">{currentWallet && Object.keys(currentWallet).length > 0 ? "Detalle Wallet" : "Nuevo Wallet"}</h6>
            </M.Header>
            <M.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col sm={12} md={6} lg={6}>
                            <Input
                                classNameContainer="mb-2" 
                                label="Código"
                                name="code"
                                id="code"
                                text={`Min ${CODE_MIN_LENGTH} - Max ${CODE_MAX_LENGTH} caracteres`}
                                maxLength={15}
                                value={code}
                                onChange={handleChange}
                                autoFocus
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <Select
                                id="statusEvent"
                                classNameContainer="mb-2"
                                label="Estatus"
                                classNameLabel="w-100"
                                options={activeEnum.map((item) => ({ value: item.value, label: item.label }))}
                                value={statusEvent}
                                onChange={handleSelect("statusEvent")}
                                style={{ width: "100%" }}
                            />
                        </Col>


                        <Col sm={12} md={6} lg={6}>
                            <Input
                                classNameContainer="mb-2" 
                                label={`Usos ${currentWallet && Object.keys(currentWallet).length > 0 ? "Restantes" : ""}`}
                                name="description"
                                id="description"
                                value={remaining_uses}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <DatePicker
                                classNameContainer="mb-2"
                                classNameLabel="w-100"
                                label="Inicia"
                                onChange={handleDateTimePicker("start")}
                                value={startAt}
                                style={{ width: "100%" }}
                                format={'DD/MMM/YYYY'}
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <DatePicker
                                classNameContainer="mb-2"
                                classNameLabel="w-100"
                                label="Expira"
                                onChange={handleDateTimePicker("expire")}
                                value={expireAt}
                                style={{ width: "100%" }}
                                format={'DD/MMM/YYYY'}
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <Select
                                id="type"
                                classNameContainer="mb-2"
                                label="Tipo"
                                classNameLabel="w-100"
                                options={walletTypeEnum}
                                value={type}
                                onChange={handleSelect("type")}
                                style={{ width: "100%" }}
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                            <Input
                                classNameContainer="mb-2" 
                                label="Marca"
                                name="brand"
                                id="brand"
                                maxLength={15}
                                value={brand}
                                onChange={handleChange}
                                autoFocus
                            />
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                        {
                            type === "Amount" ?
                                <Input
                                    classNameContainer="mb-2" 
                                    label="Monto"
                                    classNameLabel="w-100"
                                    style={{ width: "100%" }}
                                    name="amount"
                                    id="discount"
                                    value={`$${formatCurrency(discount)}`}
                                    onChange={handleNumberInput}
                                />
                            :
                            type === "Product" &&
                            <Select
                                id="product_id"
                                classNameContainer="mb-2"
                                label="Producto"
                                classNameLabel="w-100"
                                options={products.data?.map((item: any) => ({ value: item.id, label: item.name?.es })).sort((a:any, b:any) => a.label.localeCompare(b.label)) || []}
                                value={productId}
                                onChange={handleSelect("product_id")}
                                style={{ width: "100%" }}
                                showSearch
                                placeholder="Buscar producto..."
                                filterOption={(input: string, option: any) =>
                                {
                                    return option?.children?.toLowerCase().includes(input.toLowerCase())
                                }
                                }
                                allowClear
                            />
                        }

                        </Col>




                        <Divider style={{ margin: "16px 0"}} />
                        <Col style={{ textAlign: "right"}} sm={12} md={{ offset: 6, span: 6}} lg={{ offset: 6, span: 6}}>
                            <Button size="sm" className="me-3 pry-outline-btn"
                                onClick={handleHideModal}
                            >
                                {"Cancelar"}
                            </Button>
                            <Button size="sm" type="submit" className="primary-btn"
                                // disabled={isDisabled}
                                loading={loading}
                            >
                                {"Enviar"}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </M.Body>
        </Modal>
    )
}
export default AddWallet;